import {MembershipBucket, ProductNames, Tiers} from '../models/SubscriptionsModels';
import {featuresForPlan, LeadingDuration, PLAN_DEFS, PlanDef, SOLO_PREMIUM_PLUS_FEATURES} from '../models/PlansConfig';
import {Partners} from '../models/PartnersModel';
import {TrialCodes, trialCodeToDays} from '../services/subscription.service';

export interface MembershipCard {
    tier: Tiers;
    membership: MembershipBucket;
    name: ProductNames;
    price: string;
    duration: string;
    buttonClass: string;
    isPopular: boolean;
    features: { text: string; icon: string; isAvailable: boolean }[];
    buttonAction: () => void;
    buttonText: string;
    planDef: PlanDef;
};

export enum MembershipPage {
    Upgrade = 'upgrade',
    Signup = 'signup'
}
export interface BuildMembershipsCtx {
    page: MembershipPage;
    isAuthenticated: boolean;
    partner?: Partners;
    onDowngrade: () => void;
    onUpgrade: (tier: Tiers) => void;
    navigateToLogin: () => void;
    onFreeSignupOpenModal?: () => void;
    couponDiscount?: string;
    couponAmountOff?: number;
    couponPercentOff?: number;
    currentMembershipBucket?: MembershipBucket;
    trialCode?: string;
};

export interface PriceRule {
    name: string;
    condition: (plan: PlanDef, ctx: BuildMembershipsCtx) => boolean;
    getLabels: (plan: PlanDef, ctx: BuildMembershipsCtx) => { price: string; duration: string };
}

const PREMIUM_SLUGS = new Set(['premiumupgradeoptions', 'premiumsaleupgrade', 'planoptions']);
const FREE_SLUGS = new Set(['14daytrial', '6monthsfree']);

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n: number) => usd.format(Math.round(n));

const resolveDuration = (d: LeadingDuration, page: MembershipPage): string =>
    typeof d === 'string' ? d : (d?.[page] ?? '');

const isPaid = (plan: PlanDef): plan is PlanDef & { price: number } => plan.price !== null;

const isCurrentPlan = (plan: PlanDef, ctx: BuildMembershipsCtx) =>
    !!ctx.currentMembershipBucket && ctx.currentMembershipBucket === plan.membership;

const hasCoupon = (ctx: BuildMembershipsCtx) =>
    ctx.couponAmountOff != null || ctx.couponPercentOff != null;

export const isTrialCode = (code?: string): code is TrialCodes => {
    return (Object.values(TrialCodes) as string[]).includes(code);
};



const calcDiscount = (price: number, amountOff?: number, percentOff?: number) => {
    let result = price;
    if (amountOff != null) result = price - amountOff;
    else if (percentOff != null) result = price - price * (percentOff / 100);
    return Math.max(0, Math.round(result));
};

export const priceRules: PriceRule[] = [
    {
        name: 'Trial Code',
        condition: (plan, ctx) => isPaid(plan) && isTrialCode(ctx.trialCode),
        getLabels: (plan, ctx) => {
            const annual = plan.price!;
            const trial = trialCodeToDays(ctx.trialCode);

            return {
                price: `Try for $1`,
                duration: `$${annual}/yr after ${trial} trial`
            };
        }
    },

    {
        name: 'Premium Partner Upgrade',
        condition: (plan, ctx) => isPaid(plan) && !!ctx.partner?.name && PREMIUM_SLUGS.has(ctx.partner.name),
        getLabels: (plan) => {
            const annual = plan.price!;
            return {
                price: `$${annual / 12}/mo`,
                duration: `Billed annually at $${annual}`
            };
        }
    },
    // {
    //     name: 'Coupon Discount',
    //     condition: (plan, ctx) => isPaid(plan) && !isCurrentPlan(plan, ctx) && hasCoupon(ctx),
    //     getLabels: (plan, ctx) => {
    //         const discounted = calcDiscount(plan.price!, ctx.couponAmountOff ?? undefined, ctx.couponPercentOff ?? undefined);
    //         return { price: `Pay $${discounted}`, duration: '' };
    //     }
    // },
    {
        name: 'Modal Description (Free slugs or Partner View on paid)',
        condition: (plan, ctx) =>
            isPaid(plan) && (
                (!!ctx.partner?.name && FREE_SLUGS.has(ctx.partner.name)) ||
                !!ctx.partner?.isPartnerView
            ),
        getLabels: (plan, ctx) => ({
            price: ctx.partner?.modalDescription || fmt(plan.price!),
            duration: resolveDuration(plan.defaultDuration, ctx.page)
        })
    },
    {
        name: 'Partner Trial',
        condition: (plan, ctx) => isPaid(plan) && !!ctx.partner?.trialDays && ctx.partner.trialDays > 0,
        getLabels: (plan, ctx) => {
            const base = fmt(plan.price!);
            return {
                price: ctx.partner?.modalDescription ?? base,
                duration: `${base}/yr after ${ctx.partner!.trialDays}-day trial`
            };
        }
    },
    {
        name: 'Default',
        condition: () => true,
        getLabels: (plan, ctx) => ({
            price: isPaid(plan) ? fmt(plan.price!) : 'FREE',
            duration: resolveDuration(plan.defaultDuration, ctx.page)
        })
    }
];

export function computeLabels(plan: PlanDef, ctx: BuildMembershipsCtx) {
    const rule = priceRules.find(r => r.condition(plan, ctx))!;
    return rule.getLabels(plan, ctx);
}
function resolveLeadingFeatures(def: PlanDef, ctx: BuildMembershipsCtx): string[] {
    const f = def.features;
    if (Array.isArray(f)) return f;
    return f?.[ctx.page] ?? [];
}


function computeButton(planName: ProductNames, ctx: BuildMembershipsCtx): () => void {
    const isFree = planName === ProductNames.Basic;

    if (isFree) {
        if (ctx.page === MembershipPage.Upgrade) {
            return () => (ctx.isAuthenticated ? ctx.onDowngrade() : ctx.navigateToLogin());
        }
        return () => ctx.onFreeSignupOpenModal?.();
    }
    const tier = PLAN_DEFS.find(plan => plan.name === planName)!.tier;

    return () => ctx.onUpgrade(tier);
}

export function computeButtonText(
    planName: ProductNames,
    ctx: BuildMembershipsCtx,
    isCurrentPlan: boolean
): string {
    if (isCurrentPlan) {
        return 'Your Current Plan';
    }
    const { currentMembershipBucket, page, couponDiscount, partner } = ctx;

    const userOnTopTier = currentMembershipBucket === MembershipBucket.PremiumPlus;
    const userOnMiddleTier = currentMembershipBucket === MembershipBucket.Premium;

    const isFreeTier = planName === ProductNames.Basic;
    const isTopTier  = planName === ProductNames.PremiumPlus;

    //display trial amount for trialCodes in the URL
    if(ctx.trialCode && !isFreeTier){
        return "Try for $1"
    }
    //display coupon discount on button
    // if (couponDiscount && [ProductNames.Premium, ProductNames.PremiumPlus].includes(planName)) {
    //     return `Get ${couponDiscount} off`;
    // }
    //if user is on highest tier, display "downgrade" for both lower tier buttons
    if (page === MembershipPage.Upgrade && userOnTopTier && !isTopTier) {
        return 'Downgrade';
    }
    //if user is on middle tier, display "upgrade" for higher tier, and "downgrade" for lower unless special case
    if (page === MembershipPage.Upgrade && userOnMiddleTier && partner.name && !PREMIUM_SLUGS.has(partner.name) ) {
        return isFreeTier ? 'Downgrade' : 'Upgrade';
    }

    // Plan-specific defaults
    switch (planName) {
        case ProductNames.PremiumPlus:
            //normal pplus button
                return partner?.btnPremium;
            case  ProductNames.Premium:
            //normal prem button
                return partner?.btnPremium;
        case ProductNames.Basic:
            //display "downgrade" on free button for upgrade page
            return page === MembershipPage.Upgrade
                ? 'Downgrade'
                //display waitlist option for signup plan selection page
                :  'Join Waitlist';
        default:
            return 'Join Waitlist';
    }
}

export function buildMemberships(ctx: BuildMembershipsCtx): MembershipCard[] {
    return PLAN_DEFS.map(def => {
        const leadingTexts = resolveLeadingFeatures(def, ctx)
        const leading = leadingTexts.map(text => ({ text, icon: 'pi pi-check', isAvailable: true }));
        const base = featuresForPlan(def.name, undefined, !!def.forceAllBaseAvailable);
        const isSignup = ctx.page === MembershipPage.Signup
        const soloMode = isSignup && ctx.partner?.shouldShowPremiumPlusOffer && !ctx.partner?.shouldShowContinueBtn && def.name === ProductNames.PremiumPlus

        const { price, duration } = computeLabels(def, ctx);

        return {
            tier: def.tier,
            membership: def.membership,
            name: def.name,
            price,
            duration,
            buttonClass: def.buttonClass,
            isPopular: def.isPopular,
            features: soloMode ? SOLO_PREMIUM_PLUS_FEATURES : [...leading, ...base],
            buttonAction: computeButton(def.name, ctx),
            buttonText: computeButtonText(def.name, ctx, false),
            planDef: def
        };
    });
}
